import { createStore } from 'zustand/vanilla'
import type { FeedHealth, SystemStatus } from '@traintopia/contracts'

// Upstream feed liveness and headway summary, pushed over the WebSocket as SYSTEM_STATUS
// on every poll cycle (and once on connect). Session-only, like the transit store.

export type { FeedHealth, RouteHeadwayStat, SystemStatus } from '@traintopia/contracts'

interface SystemState {
  status: SystemStatus | null
  /**
   * performance.now() when `status` arrived. The server reports feed ages as durations,
   * aged forward locally from here rather than diffed against `lastSuccessMs`, so device
   * clock skew never enters the calculation and a dead poller keeps aging on screen instead
   * of freezing at whatever it last claimed.
   */
  receivedAtMs: number | null
  setStatus: (status: SystemStatus) => void
}

export const useSystemStore = createStore<SystemState>((set) => ({
  status: null,
  receivedAtMs: null,
  setStatus: (status) => set({ status, receivedAtMs: performance.now() }),
}))

/**
 * A feed is stale once its last success is older than this. Well above both poll cadences,
 * since a single dropped poll is normal and shouldn't light up the readout.
 */
export const FEED_STALE_SECONDS = 90

/** Server-reported age, advanced by however long ago this client received it. */
export function liveAgeSeconds(feed: FeedHealth, receivedAtMs: number | null): number | null {
  if (feed.ageSeconds === null) return null
  if (receivedAtMs === null) return feed.ageSeconds
  return feed.ageSeconds + Math.max(0, (performance.now() - receivedAtMs) / 1000)
}

export function isFeedStale(feed: FeedHealth, receivedAtMs: number | null): boolean {
  const age = liveAgeSeconds(feed, receivedAtMs)
  return age === null || age > FEED_STALE_SECONDS
}

/** Worst-case view of the whole upstream: what a single summary indicator should show. */
export function overallFeedState(
  status: SystemStatus | null,
  receivedAtMs: number | null,
): 'ok' | 'degraded' | 'down' | 'unknown' {
  if (!status || status.feeds.length === 0) return 'unknown'
  const bad = status.feeds.filter((f) => !f.ok || isFeedStale(f, receivedAtMs)).length
  if (bad === 0) return 'ok'
  if (bad === status.feeds.length) return 'down'
  return 'degraded'
}
